import type { ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
  type SvgIconProps,
} from '@mui/material';

interface StatTileProps {
  label: string;
  value: ReactNode;
  icon?: React.ComponentType<SvgIconProps>;
  footer?: ReactNode;
  loading?: boolean;
  onClick?: () => void;
}

/** A single headline number with its label (and an optional footer chip/progress). */
export function StatTile({ label, value, icon: Icon, footer, loading, onClick }: StatTileProps) {
  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : undefined,
        '&:hover': onClick
          ? { borderColor: 'primary.light', transform: 'translateY(-2px)' }
          : undefined,
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          {Icon && (
            <Box
              sx={(theme) => ({
                width: 32,
                height: 32,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: theme.vars.palette.primary.container,
                color: theme.vars.palette.primary.onContainer,
              })}
            >
              <Icon sx={{ fontSize: 18 }} />
            </Box>
          )}
        </Stack>
        <Typography variant="h3" component="p" sx={{ mt: 0.5, mb: footer ? 1.25 : 0 }}>
          {loading ? <Skeleton width={64} /> : value}
        </Typography>
        {footer && (loading ? <Skeleton variant="rounded" width={110} height={22} /> : footer)}
      </CardContent>
    </Card>
  );
}
